import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getProductRef, getUserRef } from '../firebase/collections';
import { firebase } from '../firebase/config';
import { AuthContext } from './AuthContext';

export const WatchlistContext = React.createContext(null);

function WatchlistProvider({ children }) {
  const { user } = React.useContext(AuthContext);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    if (!user?.uid) {
      setWatchlist([]);
      return;
    }
    const unsub = getUserRef(user.uid).onSnapshot(
      (doc) => {
        const data = doc.exists ? doc.data() : {};
        setWatchlist(data.watchlist || []);
      },
      (err) => {
        console.error('WatchlistContext listener error:', err);
        setWatchlist([]);
      }
    );
    return () => unsub();
  }, [user?.uid]);

  const addToWatchlist = useCallback(
    (productId, alertPrice) => {
      if (!user?.uid || !productId)
        return Promise.reject(new Error('Usuário não autenticado'));
      const price = Number(alertPrice) || 0;
      const entry = {
        productId,
        alertPrice: price,
        addedAt: Date.now(),
      };
      const userRef = getUserRef(user.uid);
      return userRef.get().then((doc) => {
        const list = (doc.exists ? doc.data().watchlist : []) || [];
        const existingIdx = list.findIndex(
          (e) => (typeof e === 'string' ? e : e?.productId) === productId
        );
        let updatedList = [...list];
        if (existingIdx >= 0) {
          updatedList[existingIdx] = entry;
        } else {
          updatedList.push(entry);
        }
        return userRef
          .set({ watchlist: updatedList }, { merge: true })
          .then(() => {
            getProductRef(productId)
              .update({
                [`watchers.${user.uid}`]: price,
              })
              .catch((err) => {
                console.warn('Could not update watchers on product doc:', err);
              });
          });
      });
    },
    [user?.uid]
  );

  const removeFromWatchlist = useCallback(
    (productId) => {
      if (!user?.uid || !productId)
        return Promise.reject(new Error('Usuário não autenticado'));
      const userRef = getUserRef(user.uid);
      return userRef.get().then((doc) => {
        const list = (doc.exists ? doc.data().watchlist : []) || [];
        const updatedList = list.filter(
          (e) => (typeof e === 'string' ? e : e?.productId) !== productId
        );
        return userRef
          .set({ watchlist: updatedList }, { merge: true })
          .then(() => {
            getProductRef(productId)
              .update({
                [`watchers.${user.uid}`]:
                  firebase.firestore.FieldValue.delete(),
              })
              .catch((err) => {
                console.warn('Could not remove watcher on product doc:', err);
              });
          });
      });
    },
    [user?.uid]
  );

  const isWatching = useCallback(
    (productId) =>
      watchlist.some(
        (e) => (typeof e === 'string' ? e : e?.productId) === productId
      ),
    [watchlist]
  );

  const getWatchEntry = useCallback(
    (productId) =>
      watchlist.find(
        (e) => (typeof e === 'string' ? e : e?.productId) === productId
      ),
    [watchlist]
  );

  const value = useMemo(
    () => ({
      watchlist,
      addToWatchlist,
      removeFromWatchlist,
      isWatching,
      getWatchEntry,
    }),
    [watchlist, addToWatchlist, removeFromWatchlist, isWatching, getWatchEntry]
  );

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
}

export default WatchlistProvider;
